import {ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as fromUser from './user.reducer';
import * as fromDevice from './device.reducer';
import * as fromOrganizationToken from './organization-token.reducer';
import * as fromOrganizationUser from './organization-user.reducer';
import * as fromAirDataPoll from './air-data-poll.reducer';
import * as fromFloorPlan from './floor-plan.reducer';
import {OrganizationUser} from '../models/organization-user';
import {OrganizationUserRole} from '../enums/organization-user-role.enum';
import {OrganizationUserStatus} from '../enums/organization-user-status.enum';
import {Device, DeviceWithPolledAirDatum, DeviceWithUuid} from '../models/device';
import {Floor} from '../models/floor';
import {PolledAirDatum} from '../models/air-data';

export interface AfbApiState {
  user: fromUser.State;
  device: fromDevice.State;
  organizationToken: fromOrganizationToken.State;
  organizationUser: fromOrganizationUser.State;
  airDataPoll: fromAirDataPoll.State;
  floorPlan: fromFloorPlan.State;
  // group: fromGroup.State;
}

export interface State extends fromRoot.State {
  afbApi: AfbApiState;
}

export const reducers: ActionReducerMap<AfbApiState> = {
  user: fromUser.reducer,
  device: fromDevice.reducer,
  organizationToken: fromOrganizationToken.reducer,
  organizationUser: fromOrganizationUser.reducer,
  airDataPoll: fromAirDataPoll.reducer,
  floorPlan: fromFloorPlan.reducer
  // group: fromGroup.reducer
};

/**
 * The createFeatureSelector function selects a piece of state from the root of the state object.
 * This is used for selecting feature states that are loaded eagerly or lazily.
 */
export const afbApiState = createFeatureSelector<AfbApiState>('afbApi');

/**
 * Device State
 */
export const deviceState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.device
);

export const deviceEntities = createSelector(
  deviceState,
  fromDevice.deviceEntities
);
export const isGetDevicesLoading = createSelector(
  deviceState,
  fromDevice.isGetDevicesLoading
);
export const isGetDevicesLoaded = createSelector(
  deviceState,
  fromDevice.isGetDevicesLoaded
);
export const getDevicesError = createSelector(
  deviceState,
  fromDevice.getDevicesError
);
export const isPostDeviceNamesLoading = createSelector(
  deviceState,
  fromDevice.isPostDeviceNamesLoading
);
export const isPostDeviceNamesLoaded = createSelector(
  deviceState,
  fromDevice.isPostDeviceNamesLoaded
);
export const postDeviceNamesError = createSelector(
  deviceState,
  fromDevice.postDeviceNamesError
);
export const isPostShowDevicesLedLoading = createSelector(
  deviceState,
  fromDevice.isPostShowDevicesLedLoading
);
export const isPostShowDevicesLedLoaded = createSelector(
  deviceState,
  fromDevice.isPostShowDevicesLedLoaded
);
export const postShowDevicesLedError = createSelector(
  deviceState,
  fromDevice.postShowDevicesLedError
);
export const isPostHideDevicesLedLoading = createSelector(
  deviceState,
  fromDevice.isPostHideDevicesLedLoading
);
export const isPostHideDevicesLedLoaded = createSelector(
  deviceState,
  fromDevice.isPostHideDevicesLedLoaded
);
export const postHideDevicesLedError = createSelector(
  deviceState,
  fromDevice.postHideDevicesLedError
);

export const devices = createSelector(
  deviceEntities,
  (entities: { [deviceUuid: string]: Device }) => {
    return Object.keys(entities).reduce((acc: Device[], deviceUuid: string) => [...acc, entities[deviceUuid]], []);
  });
export const devicesWithUuid = createSelector(
  deviceEntities,
  (entities: { [deviceUuid: string]: Device }) => {
    return Object.keys(entities).reduce((acc: DeviceWithUuid[], deviceUuid: string) => [
      ...acc,
      {
        ...entities[deviceUuid],
        uuid: deviceUuid
      }
    ], []);
  }
);

/**
 * Organization Token State
 */
export const organizationTokenState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.organizationToken
);

export const organizationTokenEntities = createSelector(
  organizationTokenState,
  fromOrganizationToken.organizationTokenEntities
);
export const isGetOrganizationTokenLoading = createSelector(
  organizationTokenState,
  fromOrganizationToken.isGetOrganizationTokenLoading
);
export const isGetOrganizationTokenLoaded = createSelector(
  organizationTokenState,
  fromOrganizationToken.isGetOrganizationTokenLoaded
);
export const getOrganizationTokenError = createSelector(
  organizationTokenState,
  fromOrganizationToken.getOrganizationTokenError
);

/**
 * User State
 */
export const userState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.user
);

export const user = createSelector(
  userState,
  fromUser.user
);
export const isGetUserLoading = createSelector(
  userState,
  fromUser.isGetUserLoading
);
export const isGetUserLoaded = createSelector(
  userState,
  fromUser.isGetUserLoaded
);
export const getUserError = createSelector(
  userState,
  fromUser.getUserError
);
export const isPostUserLoading = createSelector(
  userState,
  fromUser.isPostUserLoading
);
export const isPostUserLoaded = createSelector(
  userState,
  fromUser.isPostUserLoaded
);
export const postUserError = createSelector(
  userState,
  fromUser.postUserError
);

/**
 * Organization User State
 */
export const organizationUserState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.organizationUser
);

export const organizationUserEntities = createSelector(
  organizationUserState,
  fromOrganizationUser.organizationUserEntities
);
export const selfOrganizationUser = createSelector(
  organizationUserState,
  fromOrganizationUser.selfOrganizationUser
);
export const isGetOrganizationUsersLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isGetOrganizationUsersLoading
);
export const isGetOrganizationUsersLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isGetOrganizationUsersLoaded
);
export const getOrganizationUsersError = createSelector(
  organizationUserState,
  fromOrganizationUser.getOrganizationUsersError
);
export const isGetSelfOrganizationUserLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isGetSelfOrganizationUserLoading
);
export const isGetSelfOrganizationUserLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isGetSelfOrganizationUserLoaded
);
export const getSelfOrganizationUserError = createSelector(
  organizationUserState,
  fromOrganizationUser.getSelfOrganizationUserError
);
export const isPostOrganizationInvitationLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isPostOrganizationInvitationLoading
);
export const isPostOrganizationInvitationLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isPostOrganizationInvitationLoaded
);
export const postOrganizationInvitationError = createSelector(
  organizationUserState,
  fromOrganizationUser.postOrganizationInvitationError
);
export const isPostGroupInvitationLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isPostGroupInvitationLoading
);
export const isPostGroupInvitationLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isPostGroupInvitationLoaded
);
export const postGroupInvitationError = createSelector(
  organizationUserState,
  fromOrganizationUser.postGroupInvitationError
);
export const isDeletePendingOrganizationUserLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isDeletePendingOrganizationUserLoading
);
export const isDeletePendingOrganizationUserLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isDeletePendingOrganizationUserLoaded
);
export const deletePendingOrganizationUserError = createSelector(
  organizationUserState,
  fromOrganizationUser.deletePendingOrganizationUserError
);
export const isDeletePendingOrganizationMemberLoading = createSelector(
  organizationUserState,
  fromOrganizationUser.isDeletePendingOrganizationMemberLoading
);
export const isDeletePendingOrganizationMemberLoaded = createSelector(
  organizationUserState,
  fromOrganizationUser.isDeletePendingOrganizationMemberLoaded
);
export const deletePendingOrganizationMemberError = createSelector(
  organizationUserState,
  fromOrganizationUser.deletePendingOrganizationMemberError
);

export const organizationUsers = createSelector(
  organizationUserEntities,
  (entities: { [id: string]: OrganizationUser }) => Object.keys(entities).reduce((acc: OrganizationUser[], key: string) => {
    return [
      ...acc,
      entities[key]
    ];
  }, [])
);
export const activeAdmins = createSelector(
  organizationUsers,
  (_organizationUsers: OrganizationUser[]) => _organizationUsers.filter((organizationUser: OrganizationUser) =>
    organizationUser.role === OrganizationUserRole.Admin && organizationUser.status === OrganizationUserStatus.Accepted)
);
export const pendingAdmins = createSelector(
  organizationUsers,
  (_organizationUsers: OrganizationUser[]) => _organizationUsers.filter((organizationUser: OrganizationUser) =>
    organizationUser.role === OrganizationUserRole.Admin && organizationUser.status === OrganizationUserStatus.Pending)
);
export const activeMembers = createSelector(
  organizationUsers,
  (_organizationUsers: OrganizationUser[]) => _organizationUsers.filter((organizationUser: OrganizationUser) =>
    organizationUser.role === OrganizationUserRole.Member && organizationUser.status === OrganizationUserStatus.Accepted)
);
export const pendingMembers = createSelector(
  organizationUsers,
  (_organizationUsers: OrganizationUser[]) => _organizationUsers.filter((organizationUser: OrganizationUser) =>
    organizationUser.role === OrganizationUserRole.Member && organizationUser.status === OrganizationUserStatus.Pending)
);

/**
 * Air Data Poll State
 */
export const airDataPollState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.airDataPoll
);

export const airDataPollEntities = createSelector(
  airDataPollState,
  fromAirDataPoll.airDataPollEntities
);
export const isAirDataPolling = createSelector(
  airDataPollState,
  fromAirDataPoll.isAirDataPolling
);

/**
 * Floor Plan State
 */
export const floorPlanState = createSelector(
  afbApiState,
  (state: AfbApiState) => state.floorPlan
);

export const floorEntities = createSelector(
  floorPlanState,
  fromFloorPlan.floorEntities
);
export const isGetFloorsLoaded = createSelector(
  floorPlanState,
  fromFloorPlan.isGetFloorsLoaded
);
export const getFloorsError = createSelector(
  floorPlanState,
  fromFloorPlan.getFloorsError
);
export const isPostFloorLoaded = createSelector(
  floorPlanState,
  fromFloorPlan.isPostFloorLoaded
);
export const postFloorError = createSelector(
  floorPlanState,
  fromFloorPlan.postFloorError
);
export const isPostFloorPlanImageLoading = createSelector(
  floorPlanState,
  fromFloorPlan.isPostFloorPlanImageLoading
);
export const isPostFloorPlanImageLoaded = createSelector(
  floorPlanState,
  fromFloorPlan.isPostFloorPlanImageLoaded
);
export const postFloorPlanImageError = createSelector(
  floorPlanState,
  fromFloorPlan.postFloorPlanImageError
);
export const isPutFloorDevicesLoading = createSelector(
  floorPlanState,
  fromFloorPlan.isPutFloorDevicesLoading
);
export const isPutFloorDevicesLoaded = createSelector(
  floorPlanState,
  fromFloorPlan.isPutFloorDevicesLoaded
);
export const putFloorDevicesError = createSelector(
  floorPlanState,
  fromFloorPlan.putFloorDevicesError
);
export const isDeleteFloorLoading = createSelector(
  floorPlanState,
  fromFloorPlan.isDeleteFloorLoading
);
export const isDeleteFloorLoaded = createSelector(
  floorPlanState,
  fromFloorPlan.isDeleteFloorLoaded
);
export const deleteFloorError = createSelector(
  floorPlanState,
  fromFloorPlan.deleteFloorError
);

export const floors = createSelector(
  floorEntities,
  (entities: { [floorId: string]: Floor }) =>
    Object.keys(entities).reduce((acc: Floor[], floorId: string) => [...acc, entities[floorId]], [])
);
export const hasFloor = createSelector(
  floors,
  (_floors: Floor[]) => (_floors.length > 0)
);

/**
 * Hybrid
 */
export const getDevicesWithPolledAirData = createSelector(
  deviceEntities,
  airDataPollEntities,
  (_deviceEntities: { [deviceUuid: string]: Device }, polledAirDataEntities: { [deviceUuid: string]: PolledAirDatum }) =>
    Object.keys(_deviceEntities).reduce((acc: DeviceWithPolledAirDatum[], deviceUuid: string) => {
      return [
        ...acc,
        {
          device: {..._deviceEntities[deviceUuid], uuid: deviceUuid},
          polledAirDatum: polledAirDataEntities[deviceUuid]
        }
      ];
    }, [])
);
